import { useCartBooking } from '@/hooks/useCartBooking';
import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { formatApiError } from '@/lib/api';
import { MapPin, Minus, Plus, Monitor, GraduationCap, X } from 'lucide-react';
const euro = n => `€${Number(n || 0).toLocaleString('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2
})}`;
const fmtShortDate = d => {
  try {
    return new Date(d).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short'
    });
  } catch {
    return '';
  }
};
const fmtLongDate = d => {
  try {
    return new Date(d).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '';
  }
};
function Stepper({
  value,
  onChange,
  min = 1,
  max = 10
}) {
  return <div className="inline-flex items-center rounded-full border border-border bg-card" data-testid="course-quantity-stepper">
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min} aria-label="Diminuisci">
        <Minus className="h-4 w-4" />
      </Button>
      <span className="w-8 text-center text-sm font-semibold tabular-nums" data-testid="course-quantity-value">{value}</span>
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 rounded-full" onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max} aria-label="Aumenta">
        <Plus className="h-4 w-4" />
      </Button>
    </div>;
}
export function CourseEnrollDialog({
  course,
  open,
  onClose
}) {
  const addToUnifiedCart = useCartBooking();
  const [sessionId, setSessionId] = useState(null);
  const [qty, setQty] = useState(1);
  const [terms, setTerms] = useState(false);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const isOnline = (course?.mode || 'online') === 'online';
  useEffect(() => {
    if (course && open) {
      setSessionId(course.sessions?.[0]?.id || null);
      setQty(1);
      setTerms(false);
      setCreatingIntent(false);
    }
  }, [course, open]);
  const session = useMemo(() => course?.sessions?.find(s => s.id === sessionId) || null, [course, sessionId]);
  const unit = Number(course?.price || 0);
  const total = unit * qty;
  if (!course) return null;
  const goCheckout = async () => {
    if (!terms) {
      toast.error('Devi accettare i termini per procedere');
      return;
    }
    if (creatingIntent) return;
    setCreatingIntent(true);
    try {
      if (await addToUnifiedCart('course', {
        course_id: course.slug || course.id,
        session_id: sessionId,
        qty
      })) onClose();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || 'Impossibile aggiungere al carrello');
    } finally {
      setCreatingIntent(false);
    }
  };
  return <Dialog open={open} onOpenChange={o => {
    if (!o) onClose();
  }}>
      <DialogContent className="max-h-[92vh] gap-0 overflow-hidden rounded-2xl border-border p-0 sm:max-w-lg" data-testid="course-enroll-dialog">
        <div className="relative h-40 w-full overflow-hidden sm:h-48">
          {course.cover ? <img src={course.cover} alt={course.title} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-primary/70 to-primary" />}
          <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)'
        }} />
          <button type="button" onClick={onClose} className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur transition-colors hover:bg-black/60" aria-label="Chiudi" data-testid="course-enroll-close">
            <X className="h-4 w-4" />
          </button>
          <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary/90 px-2.5 py-1 text-xs font-semibold text-primary-foreground backdrop-blur">
            {isOnline ? <Monitor className="h-3.5 w-3.5" /> : <MapPin className="h-3.5 w-3.5" />}{isOnline ? 'Online' : 'In presenza'}
          </span>
          <div className="absolute inset-x-4 bottom-3">
            <h2 className="font-display text-xl font-bold leading-tight text-white drop-shadow sm:text-2xl" data-testid="course-enroll-title">{course.title}</h2>
            {course.owner_name && <p className="mt-0.5 text-xs text-white/90">con {course.owner_name}</p>}
          </div>
        </div>

        <ScrollArea className="max-h-[calc(92vh-12rem)]">
          <div className="p-5">
            <div className="space-y-5" data-testid="course-select-step">
                {course.short && <p className="text-sm leading-relaxed text-muted-foreground">{course.short}</p>}

                {(course.sessions || []).length > 0 && <div>
                    <Label className="mb-2 block text-sm font-semibold text-foreground">Scegli la sessione</Label>
                    <div className="grid grid-cols-2 gap-2" data-testid="course-session-select">
                      {course.sessions.map(s => {
                  const active = s.id === sessionId;
                  const soldOut = s.left <= 0;
                  return <button key={s.id} type="button" disabled={soldOut} onClick={() => setSessionId(s.id)} data-testid={`course-session-${s.id}`} className={cn('flex flex-col items-start rounded-xl border p-3 text-left transition-colors', soldOut ? 'cursor-not-allowed border-border opacity-50' : active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-accent/40')}>
                            <span className="text-sm font-semibold capitalize text-foreground">{fmtShortDate(s.start)}{s.time ? ` · ${s.time}` : ''}</span>
                            <span className="mt-0.5 text-xs text-muted-foreground">
                              {isOnline ? 'Online' : s.location?.name || 'In presenza'}
                              {s.left < 999 && s.left > 0 ? ` · ${s.left} posti` : soldOut ? ' · esaurito' : ''}
                            </span>
                          </button>;
                })}
                    </div>
                  </div>}

                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold text-foreground">Partecipanti</Label>
                  <Stepper value={qty} onChange={setQty} />
                </div>

                <Separator />

                <div className="space-y-1.5" data-testid="course-price-summary">
                  <div className="flex justify-between text-sm text-muted-foreground"><span>{euro(unit)} × {qty}</span><span className="tabular-nums">{euro(total)}</span></div>
                  <div className="flex justify-between pt-1 text-base font-bold text-foreground"><span>Totale</span><span className="tabular-nums" data-testid="course-total">{euro(total)}</span></div>
                </div>

                <label className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Checkbox checked={terms} onCheckedChange={v => setTerms(!!v)} data-testid="cart-selection-terms" />
                  <span>Accetto i termini del servizio e la politica di rimborso.</span>
                </label>
                <Button className="h-11 w-full rounded-full text-sm font-semibold" onClick={goCheckout} data-testid="course-enroll-button">
                  <GraduationCap className="mr-2 h-4 w-4" /> Aggiungi al carrello · {euro(total)}
                </Button>
              </div>

            

            
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}
export default CourseEnrollDialog;
